import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {useIntl} from 'umi';
import {Checkbox, Justify} from "tea-component";
import {getTrendDateInfo} from '@/pages/datalab/utils/utils';
import NumberTrendChart from "@customComp/NumberTrendChart";
import './index.scss';
import {formatDisplayValue} from "@customComp/Chart/_config/_utils";

const NumberTrendTragCard = (props: any) => {
  const {
    chartData,
    onStatusChange,
    headerRight,
    isFromAdd,
    filtersArr,
    columnX,
    oldTrendStatus,
    metricInfo,
  } = props;

  const {formatMessage} = useIntl();
  
  const [trendStatus, setTrendStatus] = useState({
    showDod: true,
    showSdlw: true,
    showTrend: true,
    showChange: true,
    showStaticTime: isFromAdd,
    ...oldTrendStatus,
  });

  useEffect(() => {
    setTrendStatus({
      showDod: true,
      showSdlw: true,
      showTrend: true,
      showChange: true,
      showStaticTime: isFromAdd,
      ...(oldTrendStatus || {}),
    });
  }, [isFromAdd, oldTrendStatus]);

  // 从添加页面进来，默认勾选显示日期，将状态保持到model层
  useEffect(() => {
    if (isFromAdd) {
      onStatusChange?.({
        showDod: true,
        showSdlw: true,
        showTrend: true,
        showChange: true,
        showStaticTime: isFromAdd,
      });
    }
  }, [isFromAdd]);
  
  const formatValue = useCallback((value: any) => formatDisplayValue({
    value,
    isPercent: false,
    accuracy: metricInfo?.accuracy || 0,
    unit: metricInfo?.column_date_type === 'percent' ? 'percent' : (metricInfo?.unit || 'bmk'),
  }), [metricInfo]);

  const {metricData, metricKey, metricName, granularity, isSingle, isHourDate} = useMemo(() => {
    
    try {
      
      const {lineData, trend_metric={}, metrics} = chartData || {};
      
      // 1. 趋势图和数字卡片需要的日期粒度和是否为单点日期的信息
      const { id } = columnX?.[0] || {}; // 数字趋势图卡片只有一个日期维度
      const {column_value=[], dateType='day'} = filtersArr?.find((filter: {id: string;}) => filter.id === id) || {};
      const {granularity, isSingle} = getTrendDateInfo({value: column_value, dateType});
  
  
      // 2. 同环比或变化率
      const changData: {dod?: number; sdlw?: number; change?: number;} = {
        change: trend_metric?.change?.show_value
      };
      switch (granularity) {
        case 'day': {
          changData.dod = trend_metric?.dod?.show_value;
          changData.sdlw = trend_metric?.sdlw?.show_value;
          break;
        }
        case 'week': {
          changData.dod = trend_metric?.wow?.show_value;
          changData.sdlw = trend_metric?.swlm?.show_value;
          break;
        }
        case 'month': {
          changData.dod = trend_metric?.mom?.show_value;
          changData.sdlw = trend_metric?.smly?.show_value;
          break;
        }
      }
      return {
        metricData: {
          trendChartData: lineData?.[0]?.[0]?.metrics || [],
          dod: changData?.dod,
          sdlw: changData?.sdlw,
          change: changData?.change,
          metricValue: formatValue(trend_metric?.value),
          staticTime: trend_metric?.date || '-',
        },
        metricKey: metrics?.[0],
        metricName: metrics?.[0]?.replace(/(t[0-2]_)/, '') || '',
        // 产品修改需求: 不需要显示聚合名称
        // curMetricsName: curMetricsName + `(${MetricTypes[trend_metric?.aggregation]?.[isEn ? 'text_en' : 'text']})`,
        granularity,
        isSingle,
        isHourDate: dateType === 'hour',
      };
      
    } catch (e) {
      
      console.log("图表趋势图卡片数据转换错误: ", e);
      return { isDataError: true, isDataLoading: false };
    }
    
  }, [chartData]);
  
  // 浮窗内容也要根据 显隐状态转换
  const changeBubbleData = useMemo(() => {
    const { trend_metric={} } = chartData;
    // 3. 浮窗中内容数据
    const changeBubbleData = [];
    if (trendStatus.showChange && !isSingle) {
      changeBubbleData.push({
        titleId: 'dbl_common_change',
        thisTime: trend_metric?.change?.this_time,
        lastTime: trend_metric?.change?.last_time,
        thisValue: formatValue(trend_metric?.change?.this_value),
        lastValue: formatValue(trend_metric?.change?.last_value),
      });
    } else {
      let dod = {};
      let sdlw = {};
      switch (granularity) {
        case 'day': {
          dod = {
            titleId: `dbl_common_dod_${granularity}`,
            thisTime: trend_metric?.dod?.this_time,
            lastTime: trend_metric?.dod?.last_time,
            thisValue: formatValue(trend_metric?.dod?.this_value),
            lastValue: formatValue(trend_metric?.dod?.last_value),
          };
          sdlw = {
            titleId: `dbl_common_sdlw_${granularity}`,
            thisTime: trend_metric?.sdlw?.this_time,
            lastTime: trend_metric?.sdlw?.last_time,
            thisValue: formatValue(trend_metric?.sdlw?.this_value),
            lastValue: formatValue(trend_metric?.sdlw?.last_value),
          };
          break;
        }
        case 'week': {
          dod = {
            titleId: `dbl_common_dod_${granularity}`,
            thisTime: trend_metric?.wow?.this_time,
            lastTime: trend_metric?.wow?.last_time,
            thisValue: formatValue(trend_metric?.wow?.this_value),
            lastValue: formatValue(trend_metric?.wow?.last_value),
          };
          sdlw = {
            titleId: `dbl_common_sdlw_${granularity}`,
            thisTime: trend_metric?.swlm?.this_time,
            lastTime: trend_metric?.swlm?.last_time,
            thisValue: formatValue(trend_metric?.swlm?.this_value),
            lastValue: formatValue(trend_metric?.swlm?.last_value),
          };
          break;
        }
        case 'month': {
          dod = {
            titleId: `dbl_common_dod_${granularity}`,
            thisTime: trend_metric?.mom?.this_time,
            lastTime: trend_metric?.mom?.last_time,
            thisValue: formatValue(trend_metric?.mom?.this_value),
            lastValue: formatValue(trend_metric?.mom?.last_value),
          };
          
          sdlw = {
            titleId: `dbl_common_sdlw_${granularity}`,
            thisTime: trend_metric?.smly?.this_time,
            lastTime: trend_metric?.smly?.last_time,
            thisValue: formatValue(trend_metric?.smly?.this_value),
            lastValue: formatValue(trend_metric?.smly?.last_value),
          };
          break;
        }
      }
  
      trendStatus.showDod && changeBubbleData.push(dod);
      trendStatus.showSdlw && changeBubbleData.push(sdlw);
    }
    return changeBubbleData;
  }, [granularity, isSingle, trendStatus.showChange, trendStatus.showDod, trendStatus.showSdlw]);
  
  return (<div className={`dbl-trend-wrap`}>
    <Justify
      className='dbl-trend-wrap-header'
      left={isHourDate ? <div>
        <Checkbox value={trendStatus.showStaticTime} onChange={(value) => { setTrendStatus({...trendStatus, showStaticTime: value});onStatusChange?.({showStaticTime: value})}}>{formatMessage({id: 'dbl_common_show_static_time'})}</Checkbox>
      </div>  : <div>
        {
          isSingle ? (<>
            <Checkbox value={trendStatus.showDod && !isHourDate} onChange={(value) => { setTrendStatus({...trendStatus, showDod: value});onStatusChange?.({showDod: value})}}>{formatMessage({id: `dbl_common_dod_${granularity}`})}</Checkbox>
            <Checkbox  value={trendStatus.showSdlw && !isHourDate} onChange={(value) => { setTrendStatus({...trendStatus, showSdlw: value});onStatusChange?.({showSdlw: value})}}>{formatMessage({id: `dbl_common_sdlw_${granularity}`})}</Checkbox>
          </>) : (<Checkbox value={trendStatus.showChange} onChange={(value) => { setTrendStatus({...trendStatus, showChange: value});onStatusChange?.({showChange: value})}}>{formatMessage({id: 'dbl_common_change'})}</Checkbox>)
        }
        <Checkbox value={trendStatus.showTrend && !isHourDate} onChange={(value) => { setTrendStatus({...trendStatus, showTrend: value});onStatusChange?.({showTrend: value})}}>{formatMessage({id: 'dbl_common_trend'})}</Checkbox>
        <Checkbox value={trendStatus.showStaticTime} onChange={(value) => { setTrendStatus({...trendStatus, showStaticTime: value});onStatusChange?.({showStaticTime: value})}}>{formatMessage({id: 'dbl_common_show_static_time'})}</Checkbox>
      </div>}
      right={headerRight}
    />
    <NumberTrendChart
      {...metricData}
      changeBubbleData={changeBubbleData}
      metricNameKey={metricKey}
      metricName={metricName}
      granularity={granularity}
      trendStatus={{
        showDod: isSingle && !isHourDate && trendStatus.showDod,
        showSdlw: isSingle && !isHourDate && trendStatus.showSdlw,
        showChange: !isSingle && !isHourDate && trendStatus.showChange,
        // 筛选器日期是Hour时, 不显示 趋势图
        showTrend: trendStatus.showTrend && !isHourDate,
        showStaticTime: trendStatus?.showStaticTime,
      }}
      formatValue={formatValue}
    />
  </div>);
};

export default NumberTrendTragCard;
